import { useState, type FormEvent } from "react";
import { Lock, Eye, EyeOff, KeyRound, ShieldAlert, ArrowRight } from "lucide-react";
import { ApiError, changePassword, type CurrentUser } from "../lib/api";

type ChangePasswordPageProps = {
  token: string;
  user: CurrentUser;
  onChanged: () => void;
};

export function ChangePasswordPage({ token, user, onChanged }: ChangePasswordPageProps) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (newPassword !== confirmation) {
      setError("New password confirmation does not match");
      return;
    }
    setSubmitting(true);
    setError(undefined);
    try {
      await changePassword(token, currentPassword, newPassword);
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Unable to change password");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#07090E] relative flex flex-col items-center justify-center p-4 selection:bg-emerald-500 selection:text-slate-950 overflow-hidden">
      {/* Ambient background glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-amber-500/10 rounded-full blur-[120px] pointer-events-none" />

      <main className="w-full max-w-md relative z-10">
        <div className="glass-panel p-8 shadow-2xl border-slate-800/80 backdrop-blur-2xl">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-amber-500 to-orange-400 flex items-center justify-center font-bold text-slate-950 text-xl mx-auto mb-3 shadow-xl shadow-amber-500/20">
              <KeyRound className="w-6 h-6" />
            </div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-semibold mb-2">
              <ShieldAlert className="w-3.5 h-3.5" />
              <span>Mandatory Security Update</span>
            </div>
            <h1 className="text-xl font-bold text-white tracking-tight">Set Permanent Password</h1>
            <p className="text-slate-400 text-xs mt-1">
              Signed in as <strong className="text-white">{user.username}</strong>. Replace the temporary bootstrap password.
            </p>
          </div>

          <form onSubmit={(event) => void submit(event)} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                Current Temporary Password
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-500 absolute left-3.5 top-3" />
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                  required
                  className="modern-input w-full pl-10 pr-4 py-2.5 text-xs text-white"
                  placeholder="Enter current password"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                New Password (Min 12 chars)
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-500 absolute left-3.5 top-3" />
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  minLength={12}
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  required
                  className="modern-input w-full pl-10 pr-10 py-2.5 text-xs text-white"
                  placeholder="Create strong password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                Confirm New Password
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-500 absolute left-3.5 top-3" />
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  minLength={12}
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  required
                  className="modern-input w-full pl-10 pr-4 py-2.5 text-xs text-white"
                  placeholder="Repeat new password"
                />
              </div>
            </div>

            {error && (
              <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full py-3 text-xs font-bold flex items-center justify-center gap-2 mt-2"
            >
              <span>{submitting ? "Updating Password..." : "Update Password & Continue"}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
